import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { UploadProps } from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  FilePdfOutlined,
  InboxOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  listRakeDocuments,
  uploadRakeDocument,
  deleteRakeDocument,
} from "../../services/rakeDocuments";
import { IRakeDocument } from "../types/rake.types";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";

dayjs.extend(relativeTime);

const { Text } = Typography;

interface RakeDocumentsModalProps {
  open: boolean;
  rakeMasterId: string | null;
  onClose: () => void;
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fileKind(mimeType: string): "image" | "pdf" | "other" {
  if (mimeType?.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return "other";
}

async function downloadDocument(doc: IRakeDocument) {
  try {
    const res = await fetch(doc.fileUrl);
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toastSuccess(`Downloaded ${doc.fileName}`);
  } catch {
    window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
    toastInfo("Opened in a new tab — use your browser to save it");
  }
}

const RakeDocumentsModal = ({
  open,
  rakeMasterId,
  onClose,
}: RakeDocumentsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<IRakeDocument[]>([]);
  const [preview, setPreview] = useState<IRakeDocument | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!rakeMasterId) return;
    setLoading(true);
    try {
      setDocuments(await listRakeDocuments(rakeMasterId));
    } catch {
      toastError("Failed to load documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId]);

  useEffect(() => {
    if (open) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setPreview(null);
    }
  }, [open, fetchDocuments]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!rakeMasterId) return;
      setUploading(true);
      try {
        await uploadRakeDocument(rakeMasterId, file);
        toastSuccess(`Uploaded ${file.name}`);
        await fetchDocuments();
      } catch (error: any) {
        toastError(
          error?.response?.data?.message ||
            error?.message ||
            `Failed to upload ${file.name}`
        );
      } finally {
        setUploading(false);
      }
    },
    [rakeMasterId, fetchDocuments]
  );

  const handleDelete = useCallback(async (doc: IRakeDocument) => {
    try {
      await deleteRakeDocument(doc.rakeDocumentId);
      toastSuccess("Document deleted");
      setDocuments((prev) =>
        prev.filter((d) => d.rakeDocumentId !== doc.rakeDocumentId)
      );
    } catch (error: any) {
      toastError(error?.response?.data?.message || "Failed to delete document");
    }
  }, []);

  const uploadProps: UploadProps = useMemo(
    () => ({
      multiple: true,
      showUploadList: false,
      beforeUpload: (file) => {
        handleUpload(file as File);
        return false;
      },
    }),
    [handleUpload]
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="min(1000px, 95vw)"
        centered
        destroyOnClose
        title={
          <Space size={10} align="center">
            <CloudUploadOutlined style={{ color: "#1677ff", fontSize: 18 }} />
            <Text strong style={{ fontSize: 16 }}>
              Rake Documents
            </Text>
            <Tag color="blue" style={{ margin: 0, borderRadius: 6 }}>
              {documents.length}
            </Tag>
          </Space>
        }
      >
        <Upload.Dragger
          {...uploadProps}
          disabled={!rakeMasterId || uploading}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            {uploading ? (
              <Spin />
            ) : (
              <InboxOutlined style={{ color: "#1677ff" }} />
            )}
          </p>
          <p className="ant-upload-text">
            {uploading ? "Uploading…" : "Click or drag files here to upload"}
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
            Any file type (PDF, image, Word, Excel…). Max 25 MB each.
          </p>
        </Upload.Dragger>

        {loading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : documents.length === 0 ? (
          <Empty
            description="No documents uploaded yet"
            style={{ padding: 32 }}
          />
        ) : (
          <Row gutter={[12, 12]}>
            {documents.map((doc) => {
              const kind = fileKind(doc.mimeType);
              return (
                <Col xs={12} sm={8} md={6} key={doc.rakeDocumentId}>
                  <Card
                    hoverable
                    styles={{ body: { padding: 0 } }}
                    style={{
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      onClick={() => setPreview(doc)}
                      style={{
                        height: 120,
                        background: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                    >
                      {kind === "image" ? (
                        <img
                          src={doc.fileUrl}
                          alt={doc.fileName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : kind === "pdf" ? (
                        <FilePdfOutlined
                          style={{ fontSize: 40, color: "#ef4444" }}
                        />
                      ) : (
                        <FileOutlined
                          style={{ fontSize: 40, color: "#64748b" }}
                        />
                      )}
                    </div>
                    <div style={{ padding: "8px 10px" }}>
                      <Tooltip title={doc.label || doc.fileName}>
                        <Text
                          strong
                          ellipsis
                          style={{ fontSize: 12, display: "block" }}
                        >
                          {doc.label || doc.fileName}
                        </Text>
                      </Tooltip>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 4,
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatBytes(doc.fileSize)}
                          {doc.fileSize ? " · " : ""}
                          {dayjs(doc.createdAt).fromNow()}
                        </Text>
                        <Space size={0}>
                          <Tooltip title="Download">
                            <Button
                              type="text"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadDocument(doc);
                              }}
                            />
                          </Tooltip>
                          <Popconfirm
                            title="Delete this document?"
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(doc)}
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Popconfirm>
                        </Space>
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Modal>

      <Modal
        open={preview !== null}
        onCancel={() => setPreview(null)}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        zIndex={1100}
        title={
          preview ? (
            <Space size={8}>
              <Text strong>{preview.label || preview.fileName}</Text>
              <Tooltip title="Download">
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadDocument(preview)}
                />
              </Tooltip>
              <Tooltip title="Open in new tab">
                <a
                  href={preview.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="small" icon={<LinkOutlined />} />
                </a>
              </Tooltip>
            </Space>
          ) : (
            "Preview"
          )
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 360,
            background: "#0f172a",
            borderRadius: 8,
          }}
        >
          {!preview ? null : fileKind(preview.mimeType) === "pdf" ? (
            <iframe
              src={preview.fileUrl}
              title={preview.fileName}
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          ) : fileKind(preview.mimeType) === "image" ? (
            <Image
              src={preview.fileUrl}
              alt={preview.fileName}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
              preview={false}
            />
          ) : (
            <div style={{ textAlign: "center", padding: 40 }}>
              <FileOutlined style={{ fontSize: 56, color: "#94a3b8" }} />
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadDocument(preview)}
                >
                  Download
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default RakeDocumentsModal;
